import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaSave, FaPalette, FaLink, FaCog, FaGlobe, FaEnvelope, FaPhone, FaMapMarkerAlt } from 'react-icons/fa';
import { db } from '../../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const SettingsManager = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  const [settings, setSettings] = useState({
    general: {
      siteName: '',
      tagline: '',
      logo: '',
      favicon: '',
      metaDescription: '',
      metaKeywords: [],
    },
    contact: {
      email: '',
      phone: '',
      location: '',
      availableForHire: true,
      preferredContact: 'email',
    },
    social: {
      github: '',
      linkedin: '',
      twitter: '',
      instagram: '',
      medium: '',
      devto: '',
      stackoverflow: '',
      youtube: '',
    },
    theme: {
      primaryColor: '#6366f1',
      secondaryColor: '#a855f7',
      accentColor: '#ec4899',
      backgroundColor: '#0f172a',
      textColor: '#ffffff',
      fontFamily: 'Inter, sans-serif',
      borderRadius: '8px',
    },
    features: {
      enableAnimations: true,
      enableParticles: true,
      enableNeuralNetwork: true,
      enableCodeMatrix: true,
      enableFloatingBadges: true,
      enableLoadingScreen: true,
      enablePageTransitions: true,
      enableDarkMode: true,
      enable3DCards: true,
      enableAOS: true,
    },
    seo: {
      googleAnalyticsId: '',
      googleSearchConsole: '',
      enableSitemap: true,
      enableRobots: true,
      canonicalUrl: '',
    },
    performance: {
      imageOptimization: true,
      lazyLoading: true,
      cacheEnabled: true,
      compressionEnabled: true,
    },
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'settings', 'siteConfig');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setSettings(docSnap.data());
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'settings', 'siteConfig'), {
        ...settings,
        updatedAt: new Date().toISOString()
      });
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error saving settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (category, field, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  };

  const addKeyword = (keyword) => {
    if (!keyword.trim()) return;
    setSettings(prev => ({
      ...prev,
      general: {
        ...prev.general,
        metaKeywords: [...(prev.general.metaKeywords || []), keyword]
      }
    }));
  };

  const removeKeyword = (index) => {
    setSettings(prev => ({
      ...prev,
      general: {
        ...prev.general,
        metaKeywords: prev.general.metaKeywords.filter((_, i) => i !== index)
      }
    }));
  };

  const tabs = [
    { id: 'general', label: 'General', icon: FaCog },
    { id: 'contact', label: 'Contact Info', icon: FaEnvelope },
    { id: 'social', label: 'Social Links', icon: FaLink },
    { id: 'theme', label: 'Theme Colors', icon: FaPalette },
    { id: 'features', label: 'Features', icon: FaGlobe },
    { id: 'seo', label: 'SEO', icon: FaGlobe },
    { id: 'performance', label: 'Performance', icon: FaCog },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Site Settings</h2>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 bg-gradient-to-r from-violet-500 to-purple-500 text-white px-6 py-2 rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
        >
          <FaSave />
          {saving ? 'Saving...' : 'Save Settings'}
        </motion.button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto border-b border-gray-700 pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-violet-500 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            <tab.icon />
            {tab.label}
          </button>
        ))}
      </div>

      {/* General Settings */}
      {activeTab === 'general' && (
        <div className="space-y-4 bg-gray-800 p-6 rounded-lg">
          <div>
            <label className="block text-gray-300 mb-2">Site Name</label>
            <input
              type="text"
              value={settings.general.siteName}
              onChange={(e) => updateSetting('general', 'siteName', e.target.value)}
              className="w-full bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
              placeholder="e.g., John Doe Portfolio"
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Tagline</label>
            <input
              type="text"
              value={settings.general.tagline}
              onChange={(e) => updateSetting('general', 'tagline', e.target.value)}
              className="w-full bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
              placeholder="e.g., Full Stack + Gen AI Developer"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-300 mb-2">Logo URL</label>
              <input
                type="text"
                value={settings.general.logo}
                onChange={(e) => updateSetting('general', 'logo', e.target.value)}
                className="w-full bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
                placeholder="/logo.png"
              />
            </div>
            <div>
              <label className="block text-gray-300 mb-2">Favicon URL</label>
              <input
                type="text"
                value={settings.general.favicon}
                onChange={(e) => updateSetting('general', 'favicon', e.target.value)}
                className="w-full bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
                placeholder="/favicon.ico"
              />
            </div>
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Meta Description</label>
            <textarea
              value={settings.general.metaDescription}
              onChange={(e) => updateSetting('general', 'metaDescription', e.target.value)}
              className="w-full bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none h-24"
              placeholder="Brief description for search engines..."
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Meta Keywords</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                id="keywordInput"
                className="flex-1 bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
                placeholder="Add keyword..."
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    addKeyword(e.target.value);
                    e.target.value = '';
                  }
                }}
              />
              <button
                onClick={() => {
                  const input = document.getElementById('keywordInput');
                  addKeyword(input.value);
                  input.value = '';
                }}
                className="bg-violet-500 text-white px-4 py-2 rounded-lg hover:bg-violet-600"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {settings.general.metaKeywords?.map((keyword, index) => (
                <span
                  key={index}
                  className="bg-gray-900 text-white px-3 py-1 rounded-full flex items-center gap-2"
                >
                  {keyword}
                  <button
                    onClick={() => removeKeyword(index)}
                    className="text-red-400 hover:text-red-500"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Contact Settings */}
      {activeTab === 'contact' && (
        <div className="space-y-4 bg-gray-800 p-6 rounded-lg">
          <div>
            <label className="block text-gray-300 mb-2 flex items-center gap-2">
              <FaEnvelope /> Email
            </label>
            <input
              type="email"
              value={settings.contact.email}
              onChange={(e) => updateSetting('contact', 'email', e.target.value)}
              className="w-full bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
              placeholder="your@email.com"
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2 flex items-center gap-2">
              <FaPhone /> Phone
            </label>
            <input
              type="tel"
              value={settings.contact.phone}
              onChange={(e) => updateSetting('contact', 'phone', e.target.value)}
              className="w-full bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
              placeholder="+1 (555) 123-4567"
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2 flex items-center gap-2">
              <FaMapMarkerAlt /> Location
            </label>
            <input
              type="text"
              value={settings.contact.location}
              onChange={(e) => updateSetting('contact', 'location', e.target.value)}
              className="w-full bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
              placeholder="San Francisco, CA"
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="availableForHire"
              checked={settings.contact.availableForHire}
              onChange={(e) => updateSetting('contact', 'availableForHire', e.target.checked)}
              className="w-5 h-5"
            />
            <label htmlFor="availableForHire" className="text-gray-300">
              Available for hire
            </label>
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Preferred Contact Method</label>
            <select
              value={settings.contact.preferredContact}
              onChange={(e) => updateSetting('contact', 'preferredContact', e.target.value)}
              className="w-full bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
            >
              <option value="email">Email</option>
              <option value="phone">Phone</option>
              <option value="linkedin">LinkedIn</option>
            </select>
          </div>
        </div>
      )}

      {/* Social Links */}
      {activeTab === 'social' && (
        <div className="space-y-4 bg-gray-800 p-6 rounded-lg">
          {Object.keys(settings.social).map((platform) => (
            <div key={platform}>
              <label className="block text-gray-300 mb-2 capitalize">{platform}</label>
              <input
                type="url"
                value={settings.social[platform]}
                onChange={(e) => updateSetting('social', platform, e.target.value)}
                className="w-full bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
                placeholder={`https://${platform}.com/username`}
              />
            </div>
          ))}
        </div>
      )}

      {/* Theme Colors */}
      {activeTab === 'theme' && (
        <div className="space-y-4 bg-gray-800 p-6 rounded-lg">
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(settings.theme).map(([key, value]) => (
              <div key={key}>
                <label className="block text-gray-300 mb-2 capitalize">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </label>
                {key.includes('Color') ? (
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={value}
                      onChange={(e) => updateSetting('theme', key, e.target.value)}
                      className="w-16 h-10 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => updateSetting('theme', key, e.target.value)}
                      className="flex-1 bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
                    />
                  </div>
                ) : (
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => updateSetting('theme', key, e.target.value)}
                    className="w-full bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Features Toggle */}
      {activeTab === 'features' && (
        <div className="space-y-3 bg-gray-800 p-6 rounded-lg">
          <p className="text-gray-400 mb-4">Enable or disable site features</p>
          {Object.entries(settings.features).map(([key, value]) => (
            <div key={key} className="flex items-center justify-between py-2 border-b border-gray-700">
              <label htmlFor={key} className="text-gray-300 capitalize">
                {key.replace(/([A-Z])/g, ' $1').trim()}
              </label>
              <input
                type="checkbox"
                id={key}
                checked={value}
                onChange={(e) => updateSetting('features', key, e.target.checked)}
                className="w-5 h-5 accent-violet-500"
              />
            </div>
          ))}
        </div>
      )}

      {/* SEO Settings */}
      {activeTab === 'seo' && (
        <div className="space-y-4 bg-gray-800 p-6 rounded-lg">
          <div>
            <label className="block text-gray-300 mb-2">Google Analytics ID</label>
            <input
              type="text"
              value={settings.seo.googleAnalyticsId}
              onChange={(e) => updateSetting('seo', 'googleAnalyticsId', e.target.value)}
              className="w-full bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
              placeholder="G-XXXXXXXXXX"
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Google Search Console</label>
            <input
              type="text"
              value={settings.seo.googleSearchConsole}
              onChange={(e) => updateSetting('seo', 'googleSearchConsole', e.target.value)}
              className="w-full bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
              placeholder="Verification code"
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">Canonical URL</label>
            <input
              type="url"
              value={settings.seo.canonicalUrl}
              onChange={(e) => updateSetting('seo', 'canonicalUrl', e.target.value)}
              className="w-full bg-gray-900 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-violet-500 outline-none"
              placeholder="https://yoursite.com"
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="enableSitemap"
              checked={settings.seo.enableSitemap}
              onChange={(e) => updateSetting('seo', 'enableSitemap', e.target.checked)}
              className="w-5 h-5"
            />
            <label htmlFor="enableSitemap" className="text-gray-300">
              Enable Sitemap Generation
            </label>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="enableRobots"
              checked={settings.seo.enableRobots}
              onChange={(e) => updateSetting('seo', 'enableRobots', e.target.checked)}
              className="w-5 h-5"
            />
            <label htmlFor="enableRobots" className="text-gray-300">
              Enable Robots.txt
            </label>
          </div>
        </div>
      )}

      {/* Performance Settings */}
      {activeTab === 'performance' && (
        <div className="space-y-3 bg-gray-800 p-6 rounded-lg">
          <p className="text-gray-400 mb-4">Optimize site performance</p>
          {Object.entries(settings.performance).map(([key, value]) => (
            <div key={key} className="flex items-center justify-between py-2 border-b border-gray-700">
              <label htmlFor={key} className="text-gray-300 capitalize">
                {key.replace(/([A-Z])/g, ' $1').trim()}
              </label>
              <input
                type="checkbox"
                id={key}
                checked={value}
                onChange={(e) => updateSetting('performance', key, e.target.checked)}
                className="w-5 h-5 accent-violet-500"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SettingsManager;
